import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2Icon,
  CopyIcon,
  GlobeIcon,
  LockIcon,
  UsersIcon,
} from 'lucide-react';
import { type JSX, useEffect, useId, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Input } from '@/components/ui/input.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.js';
import { WorkspaceMemberPicker } from '@/features/workspace/WorkspaceMemberPicker.js';
import { offersSharingControls } from '@/features/workspace/workspace-access.js';
import { useWorkspaceSharing } from '@/features/workspace/useWorkspaceSharing.js';
import {
  fetchConversation,
  updateConversationAudience,
} from '@/integration/services/integration-api.js';
import {
  activeShares,
  shareUrl,
  sharesQueryOptions,
  useCreateShare,
  type RenderedPart,
  type ShareAccessMode,
} from '@/lib/api/shares.js';
import { copyToClipboard } from '@/lib/clipboard.js';
import { ApiError, apiErrorAction } from '@/lib/api-error.js';
import { notify } from '@/lib/notify.js';
import { safeHttpUrl } from '@/lib/safe-url.js';
import { outsideTabLabel } from '@/lib/share-reach.js';
import { cn } from '@/lib/utils.js';

import { ConversationSharesDisclosure } from './ConversationSharesDisclosure.js';
import {
  CONVERSATION_AUDIENCE_LEVELS,
  sameMembers,
  type ConversationVisibility,
} from './conversation-audience.js';

/** The icons the levels draw, resolved from the pure module's names. */
const LEVEL_ICON = {
  lock: LockIcon,
  users: UsersIcon,
  globe: GlobeIcon,
} as const;

/** The one sentence a sealed chat shows instead of an invitation to share. */
const SEALED_NOTE = 'This chat holds HR or pay data and stays private.';

/**
 * The audience the server holds now, from the single-conversation view: the only source of
 * `participant_ids` and `sealed`, never a list row.
 */
function audienceQueryOptions(conversationId: string) {
  return queryOptions({
    queryKey: ['conversation-audience', conversationId],
    queryFn: () => fetchConversation(conversationId),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/** The server's refusal of a write because the chat was sealed after this dialog read it. */
function isSealedRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    error.reason === 'conversation_sealed'
  );
}

/** What the snapshot will contain, as the chat that opened the dialog can count it. */
export interface SnapshotPreview {
  /** Best-effort: how many messages the snapshot will hold. Zero when the caller cannot count. */
  messageCount: number;
  /** Earliest message timestamp (ISO), for the date-range line. */
  firstMessageAt: string | null;
  /** Latest message timestamp (ISO), for the date-range line. */
  lastMessageAt: string | null;
}

type SnapshotCaptureComponent =
  (typeof import('./snapshot/SnapshotCapture.js'))['SnapshotCapture'];

/**
 * The longest a snapshot may take from the click — loading the renderer, drawing and serializing —
 * before the share goes ahead with text.
 */
const CAPTURE_DEADLINE_MS = 20_000;

/**
 * Loads the off-screen snapshot renderer, which pulls in the chart, mermaid and whiteboard code, so
 * it arrives only when someone actually creates a link, never with the sidebar that hosts this
 * dialog. It rejects on a failed import and once `deadlineAt` (epoch ms) has passed.
 */
function loadSnapshotCapture(
  deadlineAt: number,
): Promise<SnapshotCaptureComponent> {
  return Promise.race([
    import('./snapshot/SnapshotCapture.js').then(
      (module) => module.SnapshotCapture,
    ),
    new Promise<never>((_resolve, reject) =>
      window.setTimeout(
        () => reject(new Error('Snapshot renderer took too long to load')),
        Math.max(0, deadlineAt - Date.now()),
      ),
    ),
  ]);
}

/**
 * Share one conversation — the whole of it, in one dialog.
 *
 * There used to be two dialogs here, both titled `Share conversation`: one on the sidebar row's menu that
 * set the audience, one on the chat's own header that made a snapshot link. Two screens with one name is
 * a guide that cannot tell a reader which to open, and neither of them ever mentioned that the other
 * existed — so "I shared it and they still cannot see it" had two different explanations depending on
 * which one you had found.
 *
 * They are one dialog with two tabs now, because they are two different acts. Inside the workspace it
 * hands out ACCESS to the living conversation: a grant, revocable, that keeps the thread where it is.
 * Outside it hands out a read-only SNAPSHOT — a copy of what has been said so far, which stops being the
 * author's the moment somebody opens the link. The tabs are what say so; stacked in one column they read
 * as one ladder of reach, which they are not.
 *
 * The body mounts only while the dialog is open, so each open reads the conversation's current audience
 * from the server and seeds the form from it — no sync effect, no stale carry-over between opens.
 *
 * A chat is sealed when its view says so or a write was refused as sealed. This one flag drives both the
 * sentence under the title and the unavailable controls. A seal never ends, so the dialog keeps the last
 * view after a close and drops the chat's cached share links as soon as it is sealed.
 */
export function ConversationShareDialog({
  conversationId,
  workspaceId,
  initialVisibility = 'private',
  messageCount = 0,
  firstMessageAt = null,
  lastMessageAt = null,
  allowCreate = true,
  open,
  onOpenChange,
}: {
  conversationId: string;
  workspaceId: string | null;
  /** Fallback level, used only when the fetched conversation carries no `visibility` of its own. */
  initialVisibility?: ConversationVisibility;
  messageCount?: number;
  firstMessageAt?: string | null;
  lastMessageAt?: string | null;
  /** Archived conversations expose existing snapshots but no new audience/link writes. */
  allowCreate?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const sharing = useWorkspaceSharing(workspaceId);
  const offersAudience =
    allowCreate && workspaceId !== null && offersSharingControls(sharing);
  const sealedNoteId = useId();
  const queryClient = useQueryClient();
  const [refusedConversationId, setRefusedConversationId] = useState<
    string | null
  >(null);
  const viewSealed =
    useQuery({ ...audienceQueryOptions(conversationId), enabled: open }).data
      ?.sealed === true;
  const sealed = viewSealed || refusedConversationId === conversationId;

  useEffect(() => {
    if (sealed) {
      queryClient.removeQueries({
        queryKey: ['shares', conversationId],
        exact: true,
      });
    }
  }, [sealed, conversationId, queryClient]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-xl"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Share conversation</DialogTitle>
          <DialogDescription>
            {sealed ? (
              <span id={sealedNoteId}>{SEALED_NOTE}</span>
            ) : offersAudience ? (
              'Choose who in the workspace can take part, or send a read-only snapshot outside it.'
            ) : allowCreate ? (
              'Create a read-only link to a snapshot of this conversation.'
            ) : (
              'Existing read-only snapshots remain available to copy or revoke.'
            )}
          </DialogDescription>
        </DialogHeader>

        {open && (
          <ShareBody
            conversationId={conversationId}
            workspaceId={workspaceId}
            offersAudience={offersAudience}
            initialVisibility={initialVisibility}
            preview={{ messageCount, firstMessageAt, lastMessageAt }}
            allowCreate={allowCreate}
            sealed={sealed}
            sealedNoteId={sealedNoteId}
            onSealedRefusal={setRefusedConversationId}
            onOpenChange={onOpenChange}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** The half-written snapshot link, kept out of the tab so switching tabs cannot lose it. */
interface SnapshotDraft {
  accessMode: ShareAccessMode;
  name: string;
  /** The link just created, so it can be shown and copied again. Null until one is. */
  createdShareId: string | null;
}

/** The snapshot half before anyone has touched it, and what a sealed refusal puts it back to. */
const NEW_SNAPSHOT_DRAFT: SnapshotDraft = {
  accessMode: 'internal',
  name: '',
  createdShareId: null,
};

/**
 * Both halves of the dialog, and every piece of unsaved work in it.
 *
 * The drafts live HERE rather than inside a tab, because Radix unmounts the tab a user leaves. Two ways
 * to lose somebody's work follow from the split, and both are closed here: the footer sits outside the
 * tabs so `Save` is one click away from either of them, and the half-written link — its mode, its name,
 * the url it just produced — is held as a draft so coming back to the tab finds it still there.
 *
 * The footer exists only while there IS something to save, which is the same predicate that draws the
 * unsaved line. A permanent full-strength `Save` was worse than useless on the outside tab: it sat under
 * `Create link`, so two buttons looked like the primary action while doing unrelated things, and the one
 * that read as "save my link" applied an audience change that in most cases was not pending at all.
 * Nothing pending means nothing to advertise — the corner close and Escape dismiss the dialog, and
 * `Cancel` only earns its place when there is a change to abandon. Within that footer, the unsaved
 * line itself draws only while `Save` is enabled — a picked `Group` with nobody in it yet is not a
 * change the dialog can save, so it is not yet announced as one; Cancel/Save keep their place either way.
 *
 * Only the audience half waits on a fetch. The snapshot half asks the server nothing it needs before it
 * can work, so a failed or slow audience read leaves it usable — merging the two behind one gate would
 * have made a directory hiccup take away the link the old header dialog could always create.
 *
 * An open chat waits for a read made after this body mounted, so a view kept from an earlier open never
 * seeds the form. A sealed chat waits for nothing and never shows a read error: it is private for good,
 * so it drops any draft, shows Private, counts no links and offers every control as unavailable.
 */
function ShareBody({
  conversationId,
  workspaceId,
  offersAudience,
  initialVisibility,
  preview,
  allowCreate,
  sealed,
  sealedNoteId,
  onSealedRefusal,
  onOpenChange,
}: {
  conversationId: string;
  workspaceId: string | null;
  offersAudience: boolean;
  initialVisibility: ConversationVisibility;
  preview: SnapshotPreview;
  allowCreate: boolean;
  /** True when the view or a refusal says the chat is sealed; the header shows the same flag. */
  sealed: boolean;
  /** The id of the sentence that says why a sealed chat's controls are unavailable. */
  sealedNoteId: string;
  /** Records a write the server refused because the chat is sealed. */
  onSealedRefusal: (conversationId: string) => void;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [snapshot, setSnapshot] = useState<SnapshotDraft>(NEW_SNAPSHOT_DRAFT);
  const [draftVisibility, setDraftVisibility] =
    useState<ConversationVisibility | null>(null);
  const [draftIds, setDraftIds] = useState<ReadonlySet<string> | null>(null);
  const [saving, setSaving] = useState(false);

  const audienceQuery = useQuery(audienceQueryOptions(conversationId));
  /** The second shares observer feeds only the outside-tab count; snapshotPanel owns its list query. */
  const sharesQuery = useQuery(
    sharesQueryOptions(offersAudience && !sealed ? conversationId : null),
  );
  const liveShareCount = activeShares(sharesQuery.data ?? []).length;

  const audience = audienceQuery.data;
  const unavailableNoteId = sealed ? sealedNoteId : undefined;
  const savedVisibility = sealed
    ? 'private'
    : audience
      ? (audience.visibility ?? initialVisibility)
      : null;
  const savedIds = useMemo(
    () => new Set(audience?.participant_ids ?? []),
    [audience],
  );

  /**
   * A draft of `null` means untouched, so every read falls back to what the server holds. That is what
   * makes the group-wiping bug impossible rather than merely unlikely: an untouched form has nothing
   * pending, so it draws no `Save` at all, and a member the picker never showed is sent back untouched.
   */
  const visibility =
    (sealed ? null : draftVisibility) ?? savedVisibility ?? initialVisibility;
  const selectedIds = (sealed ? null : draftIds) ?? savedIds;

  const toggleMember = (userId: string, selected: boolean): void => {
    const next = new Set(selectedIds);
    if (selected) next.add(userId);
    else next.delete(userId);
    setDraftIds(next);
  };

  /**
   * Apply the pending audience, then refresh both lists that show it. The sidebar reads the
   * workspace-scoped `['recents', workspaceId]` list and not `['conversations']`, so without the second
   * invalidation the chat stays in its old lane until a reload; the bare prefix matches every workspace.
   */
  async function save(): Promise<void> {
    setSaving(true);
    try {
      await updateConversationAudience(conversationId, {
        visibility,
        participantIds: visibility === 'group' ? [...selectedIds] : undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ['conversations'] });
      await queryClient.invalidateQueries({ queryKey: ['recents'] });
      notify.success('Audience updated');
      onOpenChange(false);
    } catch (reason) {
      notify.error(apiErrorAction(reason, 'Failed to update audience'));
      refreshIfSealed(reason);
    } finally {
      setSaving(false);
    }
  }

  /**
   * A refusal because the chat was sealed meanwhile turns the dialog sealed at once and clears the
   * half-written link, then re-reads the chat and the sidebar lists.
   */
  function refreshIfSealed(error: unknown): void {
    if (!isSealedRefusal(error)) return;
    onSealedRefusal(conversationId);
    setSnapshot(NEW_SNAPSHOT_DRAFT);
    void queryClient.invalidateQueries({
      queryKey: ['conversation-audience', conversationId],
    });
    void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    void queryClient.invalidateQueries({ queryKey: ['recents'] });
  }

  const snapshotPanel = (
    <SnapshotPanel
      conversationId={conversationId}
      workspaceId={workspaceId}
      draft={snapshot}
      onDraftChange={setSnapshot}
      preview={preview}
      allowCreate={allowCreate}
      sealedNoteId={unavailableNoteId}
      onCreateError={refreshIfSealed}
    />
  );

  if (!sealed && !audienceQuery.isFetchedAfterMount) {
    return (
      <div
        className="rounded-lg border border-border bg-muted/30 p-3"
        aria-busy
      >
        <p className="text-sm font-medium">Checking conversation privacy</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Sharing stays unavailable until the current conversation state is
          loaded.
        </p>
      </div>
    );
  }

  if (audienceQuery.isError && !sealed) {
    return (
      <div role="alert" className="rounded-lg border border-destructive/30 p-3">
        <p className="text-sm font-medium text-destructive">
          Sharing is temporarily unavailable
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          The current privacy state could not be verified. Close this dialog and
          try again; no audience or share-link change was made.
        </p>
      </div>
    );
  }

  if (!sealed && audience === undefined) {
    return (
      <div role="alert" className="rounded-lg border border-destructive/30 p-3">
        <p className="text-sm font-medium text-destructive">
          Sharing is temporarily unavailable
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          The server did not return a verifiable conversation state. Close this
          dialog and try again; no audience or share-link change was made.
        </p>
      </div>
    );
  }

  /**
   * A personal workspace has nobody to grant access to, so the audience tab would be a tab onto an empty
   * question — and a lone tab beside it reads as a control somebody forgot to finish. The snapshot half
   * is the whole dialog there, exactly as the chat header always offered it: a chat of one person is
   * still a chat somebody may want to send out.
   */
  if (!offersAudience || workspaceId === null) return snapshotPanel;

  /** A group of nobody is refused by the service, so the button says so before the round trip. */
  const canSave =
    !saving && !(visibility === 'group' && selectedIds.size === 0);

  /**
   * Whether `Save` still has anything to apply. It is said out loud because the other tab hides the
   * levels that were changed, and a change a person cannot see is a change they will walk away from.
   */
  const unsaved =
    savedVisibility !== null &&
    (visibility !== savedVisibility ||
      (visibility === 'group' && !sameMembers(selectedIds, savedIds)));

  /**
   * The "Unsaved change" note only draws alongside an enabled `Save`. A picked `Group` with nobody
   * in it yet is not something the dialog can save, so the footer keeps Cancel/Save but withholds
   * the note until there is a change `Save` could actually apply.
   */
  const showUnsavedNote = unsaved && canSave;

  return (
    <>
      <Tabs defaultValue="inside">
        <TabsList>
          <TabsTrigger value="inside">In this workspace</TabsTrigger>
          <TabsTrigger value="outside">
            {outsideTabLabel(liveShareCount)}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inside" className="flex flex-col gap-2">
          {savedVisibility === null ? (
            <div className="flex flex-col gap-2" aria-busy>
              {[0, 1, 2].map((row) => (
                <Skeleton key={row} className="h-9 w-full rounded-md" />
              ))}
            </div>
          ) : (
            <>
              <div
                className="flex flex-col gap-2"
                role="radiogroup"
                aria-label="Conversation visibility"
              >
                {CONVERSATION_AUDIENCE_LEVELS.map((level) => (
                  <ReachOption
                    key={level.visibility}
                    label={level.label}
                    hint={level.hint}
                    Icon={LEVEL_ICON[level.icon]}
                    selected={visibility === level.visibility}
                    unavailableNoteId={unavailableNoteId}
                    onSelect={() => setDraftVisibility(level.visibility)}
                  />
                ))}
              </div>

              {visibility === 'group' && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium">Members</span>
                  <WorkspaceMemberPicker
                    workspaceId={workspaceId}
                    selectedIds={selectedIds}
                    onToggle={toggleMember}
                  />
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="outside">{snapshotPanel}</TabsContent>
      </Tabs>

      {unsaved && (
        <DialogFooter className="sm:items-center">
          {showUnsavedNote ? (
            <span
              data-testid="conversation-share-unsaved"
              className="text-xs text-warning-600 sm:mr-auto"
            >
              Unsaved change to who can take part
            </span>
          ) : (
            <span className="sm:mr-auto" />
          )}
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={!canSave}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      )}
    </>
  );
}

/** Humanize an ISO timestamp for the preview line. */
function formatDate(iso: string | null): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** One-line preview of what the snapshot will contain. */
function previewLine({
  messageCount,
  firstMessageAt,
  lastMessageAt,
}: SnapshotPreview): string {
  if (messageCount <= 0) return 'This conversation';
  const from = formatDate(firstMessageAt);
  const to = formatDate(lastMessageAt);
  const messages = `${messageCount} message${messageCount === 1 ? '' : 's'}`;
  if (from && to && from !== to) return `${messages} · ${from} – ${to}`;
  if (from) return `${messages} · ${from}`;
  return messages;
}

/**
 * The two widths a snapshot link opens to. The wording is the file dialog's, for the same two modes: the
 * choice a person makes here and the choice they made there are the same choice, and had no business
 * being described in two different vocabularies — one of them a switch called "Restrict to Customertimes
 * employees only", whose unchecked state a reader had to work out from a double negative.
 */
const LINK_MODES = [
  {
    mode: 'internal' as const,
    label: 'Customertimes employees',
    hint: 'Anyone at Customertimes who has the link and is signed in.',
    Icon: Building2Icon,
  },
  {
    mode: 'public' as const,
    label: 'Anyone with the link',
    hint: 'No sign-in needed.',
    Icon: GlobeIcon,
  },
];

/**
 * Hand a read-only copy of the conversation to somebody the workspace does not contain.
 *
 * The preview line stays at the top and out of any disclosure: it is the only answer to "what exactly
 * will they see", and it belongs in front of the person before they press the button, not behind a fold
 * they have no reason to open.
 *
 * A seal abandons a capture in progress, so nothing it finishes later creates a link.
 */
function SnapshotPanel({
  conversationId,
  workspaceId,
  draft,
  onDraftChange,
  preview,
  allowCreate,
  sealedNoteId,
  onCreateError,
}: {
  conversationId: string;
  workspaceId: string | null;
  draft: SnapshotDraft;
  onDraftChange: (draft: SnapshotDraft) => void;
  preview: SnapshotPreview;
  allowCreate: boolean;
  /**
   * Set for a sealed chat: the id of the sentence that says why. Such a chat gets no new link and shows
   * none, not even one created earlier in this dialog, because the seal revoked them.
   */
  sealedNoteId: string | undefined;
  /** Receives a refused create, after its toast. */
  onCreateError: (error: unknown) => void;
}): JSX.Element {
  const sealed = sealedNoteId !== undefined;
  const create = useCreateShare();
  /** True while the conversation is drawn off screen for the snapshot, before the link is created. */
  const [capturing, setCapturing] = useState(false);
  /**
   * The capture in progress once its renderer's code has arrived, with the token it was started
   * under; null before that. The token travels with the renderer, so its result answers exactly that
   * capture and nothing else.
   */
  const [loadedCapture, setLoadedCapture] = useState<{
    token: symbol;
    Renderer: SnapshotCaptureComponent;
  } | null>(null);
  /** The capture in progress; a late result of an earlier one is ignored, so one click makes one link. */
  const activeCaptureRef = useRef<symbol | null>(null);
  /** Closing the dialog abandons a capture in progress, so nothing it finishes later creates a link. */
  useEffect(
    () => () => {
      activeCaptureRef.current = null;
    },
    [],
  );
  useEffect(() => {
    if (!sealed) return;
    activeCaptureRef.current = null;
    setCapturing(false);
    setLoadedCapture(null);
  }, [sealed]);
  /** When the capture in progress must hand over, in epoch ms; one deadline from the click to the end. */
  const [deadlineAt, setDeadlineAt] = useState(0);
  const createdUrl =
    draft.createdShareId !== null
      ? safeHttpUrl(shareUrl(draft.createdShareId))
      : '';

  /**
   * Forget the link on screen once it is really gone — and only then, so a failed DELETE leaves the
   * still-valid link and its copy button in place.
   */
  function dropRevokedLink(shareId: string): void {
    if (shareId === draft.createdShareId) {
      onDraftChange({ ...draft, createdShareId: null });
    }
  }

  /**
   * Draws the snapshot first; the link is created once {@link createWithRender} has the drawings. A
   * renderer that fails to load, or loads too slowly, shares the text instead.
   */
  function onCreate(): void {
    const capture = Symbol('capture');
    const captureDeadline = Date.now() + CAPTURE_DEADLINE_MS;
    activeCaptureRef.current = capture;
    setDeadlineAt(captureDeadline);
    setCapturing(true);
    loadSnapshotCapture(captureDeadline).then(
      (component) => {
        if (activeCaptureRef.current === capture) {
          setLoadedCapture({ token: capture, Renderer: component });
        }
      },
      () => createWithRender(capture, []),
    );
  }

  /** Creates the link for `capture`, unless that capture was abandoned or already answered. */
  function createWithRender(
    capture: symbol,
    renderedParts: RenderedPart[],
  ): void {
    if (activeCaptureRef.current !== capture) return;
    activeCaptureRef.current = null;
    setCapturing(false);
    setLoadedCapture(null);
    create.mutate(
      {
        conversationId,
        accessMode: draft.accessMode,
        name: draft.name,
        renderedParts,
      },
      {
        onSuccess: (share) => {
          onDraftChange({
            accessMode: draft.accessMode,
            name: '',
            createdShareId: share.shareId,
          });
          const url = safeHttpUrl(shareUrl(share.shareId));
          if (url !== '') void copyToClipboard(url, 'Share link copied');
        },
        onError: onCreateError,
      },
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <p className="text-sm text-muted-foreground">{previewLine(preview)}</p>

      {allowCreate ? (
        <div
          className="flex flex-col gap-2"
          role="radiogroup"
          aria-label="Who can open the link"
        >
          {LINK_MODES.map((option) => (
            <ReachOption
              key={option.mode}
              label={option.label}
              hint={option.hint}
              Icon={option.Icon}
              selected={draft.accessMode === option.mode}
              unavailableNoteId={sealedNoteId}
              onSelect={() =>
                onDraftChange({ ...draft, accessMode: option.mode })
              }
            />
          ))}
        </div>
      ) : null}

      {allowCreate && createdUrl !== '' && !sealed ? (
        <div className="flex min-w-0 items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-md border border-border/40 bg-muted/40 px-2 py-1 text-xs">
            {createdUrl}
          </code>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Copy share link"
            onClick={() =>
              void copyToClipboard(createdUrl, 'Share link copied')
            }
          >
            <CopyIcon className="size-4" strokeWidth={1.75} />
          </Button>
        </div>
      ) : allowCreate ? (
        <div className="flex flex-col gap-2">
          <label
            htmlFor="share-name"
            className="text-sm font-medium text-foreground"
          >
            Name{' '}
            <span className="font-normal text-muted-foreground">
              (optional)
            </span>
          </label>
          <Input
            id="share-name"
            value={draft.name}
            maxLength={100}
            placeholder="e.g. Q3 revenue review"
            aria-disabled={sealed || undefined}
            aria-describedby={sealedNoteId}
            readOnly={sealed}
            onChange={
              sealed
                ? undefined
                : (event) =>
                    onDraftChange({ ...draft, name: event.target.value })
            }
            className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          />
          <Button
            size="sm"
            aria-disabled={sealed || undefined}
            aria-describedby={sealedNoteId}
            disabled={capturing || create.isPending}
            onClick={sealed ? undefined : onCreate}
            className="self-start aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          >
            {capturing || create.isPending ? 'Creating…' : 'Create link'}
          </Button>
          {capturing && loadedCapture !== null && (
            <loadedCapture.Renderer
              conversationId={conversationId}
              deadlineAt={deadlineAt}
              onCaptured={(renderedParts) => {
                if (!sealed)
                  createWithRender(loadedCapture.token, renderedParts);
              }}
            />
          )}
        </div>
      ) : null}

      {!sealed && (
        <ConversationSharesDisclosure
          conversationId={conversationId}
          onRevoked={dropRevokedLink}
        />
      )}
    </div>
  );
}

/**
 * One selectable reach, drawn the same on both tabs. The audience levels and the link modes are different
 * questions, but they are the same KIND of question — how far does this go — so they are the same control.
 */
function ReachOption({
  label,
  hint,
  Icon,
  selected,
  unavailableNoteId,
  onSelect,
}: {
  label: string;
  hint: string;
  Icon: typeof LockIcon;
  selected: boolean;
  /**
   * Set when the option cannot be picked: the id of the sentence that says why. The option stays
   * focusable, so a keyboard user can still reach it and hear that reason.
   */
  unavailableNoteId?: string;
  onSelect: () => void;
}): JSX.Element {
  const unavailable = unavailableNoteId !== undefined;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={unavailable || undefined}
      aria-describedby={unavailableNoteId}
      onClick={unavailable ? undefined : onSelect}
      className={cn(
        'flex items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors',
        selected
          ? 'border-primary bg-primary/5'
          : unavailable
            ? 'border-border'
            : 'border-border hover:bg-accent',
        unavailable && 'cursor-not-allowed opacity-50',
      )}
    >
      <Icon
        className="size-4 shrink-0 text-muted-foreground"
        strokeWidth={1.75}
      />
      <span className="flex-1">
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
    </button>
  );
}
